import type { TCatalogFamily } from '@/mocks/gobeletcupCatalog';

// Stands in for a price list the backend does not have and the site does not publish
// (gobeletcup.fr only offers "Ajouter au devis"). Orders of magnitude are plausible for
// reusable PP cups; the tiers exist so the total reacts to quantity like a real quote
// would. Every figure shown from here is labelled as an estimate in the interface.
//
// Prototype data, CDC domain CMD. Tracked in PROTO-NOTES.md.

type TPrintTechnique = 'quadriShort' | 'quadriTall' | 'serigraphy1' | 'serigraphy2';

const VAT_RATE = 0.2;

const BASE_UNIT_PRICE_EXCL_VAT_IN_EUR: Record<TCatalogFamily, number> = {
    cocktail: 1.35,
    cup: 0.95,
    pitcher: 6.5,
    wine: 1.6,
};

const TECHNIQUE_MULTIPLIER: Record<TPrintTechnique, number> = {
    quadriShort: 1.6,
    quadriTall: 1.9,
    serigraphy1: 1,
    serigraphy2: 1.25,
};

// Descending quantity thresholds and the multiplier applied at or above each one.
const QUANTITY_TIERS: [number, number][] = [
    [5000, 0.8],
    [1000, 0.9],
    [500, 1],
    [200, 1.15],
    [0, 1.3],
];

function quantityMultiplier(quantity: number): number {
    const tier = QUANTITY_TIERS.find(([threshold]) => quantity >= threshold);
    return tier ? tier[1] : 1;
}

// Printing the second face of a pitcher. WooCommerce will carry two prices, recto only and
// recto-verso; the real figures are not known, so this stands in for the second one.
const SECOND_FACE_MULTIPLIER = 1.4;

// The paid options of the order (client's review, 2026-10-12), priced the way the client wrote
// them: the check of the visual by GOBELETCUP®'s designers, 6 € once for the order, and the express
// production, 30 % on the price and shipped within 5 working days. The personalisation by name
// (FIC-02) costs a little more per cup; its figure is an example. All of them are WooCommerce's
// to price (CMD-02: "tarifs des produits et options").
const VISUAL_CHECK_PRICE_INCL_VAT_IN_EUR = 6;
const EXPRESS_MULTIPLIER = 1.3;
const EXPRESS_LEAD_TIME_IN_WORKING_DAYS = 5;
const NOMINATIVE_SURCHARGE_EXCL_VAT_IN_EUR = 0.15;

function estimateUnitPriceExclVatInEur(params: {
    family: TCatalogFamily;
    hasTwoFaces?: boolean;
    isExpress?: boolean;
    isNominative?: boolean;
    quantity: number;
    technique: TPrintTechnique;
}): number {
    const { family, hasTwoFaces = false, isExpress = false, isNominative = false, quantity, technique } = params;
    const facesMultiplier = hasTwoFaces ? SECOND_FACE_MULTIPLIER : 1;
    const nominativeSurcharge = isNominative ? NOMINATIVE_SURCHARGE_EXCL_VAT_IN_EUR : 0;
    const expressMultiplier = isExpress ? EXPRESS_MULTIPLIER : 1;
    const price =
        (BASE_UNIT_PRICE_EXCL_VAT_IN_EUR[family] * TECHNIQUE_MULTIPLIER[technique] * quantityMultiplier(quantity) * facesMultiplier +
            nominativeSurcharge) *
        expressMultiplier;
    return Math.round(price * 100) / 100;
}

function toInclVat(amountExclVat: number): number {
    return Math.round(amountExclVat * (1 + VAT_RATE) * 100) / 100;
}

function toExclVat(amountInclVat: number): number {
    return Math.round((amountInclVat / (1 + VAT_RATE)) * 100) / 100;
}

// When the order ships, in working days, per print style: the client's own figures (review of
// 2026-10-12), whatever the quantity. Stands in for the lead time WooCommerce will transmit
// (CMD-02: "synchronisés avec les données configurées dans WooCommerce"), like the prices above.
const LEAD_TIME_IN_WORKING_DAYS: Record<TPrintTechnique, { max: number; min: number }> = {
    quadriShort: { max: 15, min: 5 },
    quadriTall: { max: 15, min: 5 },
    serigraphy1: { max: 10, min: 5 },
    serigraphy2: { max: 10, min: 5 },
};

function estimateLeadTimeInWorkingDays(technique: TPrintTechnique): { max: number; min: number } {
    return LEAD_TIME_IN_WORKING_DAYS[technique];
}

// The smallest order each print style takes, shown on its tile so the customer goes straight to the
// styles their quantity allows (client's review, 2026-10-12). The client's own figures (mail of
// 2026-10-15), standing in for WooCommerce's quantity rules (CFG-06), so nothing of this is set in
// the back-office.
const MINIMUM_QUANTITY: Record<TPrintTechnique, number> = {
    quadriShort: 1,
    quadriTall: 1,
    serigraphy1: 150,
    serigraphy2: 300,
};

function minimumQuantityOf(technique: TPrintTechnique): number {
    return MINIMUM_QUANTITY[technique];
}

export {
    estimateLeadTimeInWorkingDays,
    estimateUnitPriceExclVatInEur,
    EXPRESS_LEAD_TIME_IN_WORKING_DAYS,
    EXPRESS_MULTIPLIER,
    minimumQuantityOf,
    NOMINATIVE_SURCHARGE_EXCL_VAT_IN_EUR,
    toExclVat,
    toInclVat,
    VISUAL_CHECK_PRICE_INCL_VAT_IN_EUR,
};

export type { TPrintTechnique };
